import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { C, F, G, SH } from '../styles/tokens';

const SERVICES = [
  {
    title: 'Monthly Bookkeeping',
    desc: 'Accurate recording of all financial transactions every month — sales, purchases, expenses, and payments — using Tally, Zoho Books, or QuickBooks.',
  },
  {
    title: 'Accounts Payable & Receivable',
    desc: 'Track outstanding invoices, manage vendor payments, and follow up on customer dues to maintain healthy cash flow.',
  },
  {
    title: 'Bank Reconciliation',
    desc: 'Monthly reconciliation of bank statements with your books to identify discrepancies and maintain accurate financial records.',
  },
  {
    title: 'Financial Statements',
    desc: 'Preparation of Profit & Loss account, Balance Sheet, and Cash Flow Statement — ready for auditors, banks, and investors.',
  },
  {
    title: 'Payroll Processing',
    desc: 'Monthly salary computation, TDS deduction, PF & ESI contributions, payslip generation, and Form 16 preparation for employees.',
  },
  {
    title: 'TDS Compliance',
    desc: 'Timely deduction and deposit of TDS, filing quarterly TDS returns (24Q, 26Q), and issuing TDS certificates (Form 16/16A).',
  },
];

const STEPS = [
  { step: '01', title: 'Onboarding', desc: 'We understand your business type, transaction volume, and current accounting setup.' },
  { step: '02', title: 'Chart of Accounts', desc: 'We create a customised chart of accounts aligned with your industry and reporting needs.' },
  { step: '03', title: 'Data Collection', desc: 'Bank statements, invoices, bills, and expense receipts collected securely via WhatsApp or email.' },
  { step: '04', title: 'Bookkeeping', desc: 'All transactions recorded, categorised, and reconciled in your accounting software every month.' },
  { step: '05', title: 'MIS Reports', desc: 'Monthly management reports — P&L, balance sheet, cash flow — shared by the 10th of each month.' },
  { step: '06', title: 'Audit Support', desc: 'Year-end financial statements prepared and audit-ready documentation provided for statutory audit.' },
];

const FAQS = [
  {
    q: 'Why should I outsource accounting for my business?',
    a: 'Outsourcing accounting saves cost (no full-time accountant salary), ensures accuracy and compliance, provides access to CA expertise, and frees your time to focus on business growth. Pracco provides dedicated accountants for a fraction of the cost of an in-house hire.',
  },
  {
    q: 'What accounting software does Pracco use?',
    a: 'We work with Tally Prime, Zoho Books, QuickBooks, and Excel-based accounting. If you already use a specific software, we adapt to your system. If you\'re starting fresh, we recommend the best fit for your business size and budget.',
  },
  {
    q: 'Is bookkeeping mandatory for small businesses in India?',
    a: 'Under the Income Tax Act, businesses with turnover above ₹25 lakh and professionals with receipts above ₹10 lakh must maintain books of accounts. GST-registered businesses must also maintain prescribed records. Accurate bookkeeping is essential for filing accurate GST returns and ITR.',
  },
  {
    q: 'What documents do I need to share for monthly bookkeeping?',
    a: 'Bank statements, sales invoices, purchase bills, expense receipts, payroll data, and loan statements. We provide a simple monthly checklist so you know exactly what to send each month.',
  },
  {
    q: 'Can Pracco handle accounting for my e-commerce business?',
    a: 'Yes. We handle accounting for Amazon, Flipkart, Meesho, and other e-commerce sellers — including marketplace settlement reconciliation, TCS credit tracking, inventory accounting, and GST compliance.',
  },
  {
    q: 'What is the difference between bookkeeping and accounting?',
    a: 'Bookkeeping is the recording of day-to-day financial transactions. Accounting involves interpreting, classifying, analysing, and reporting those records — including preparing financial statements, tax computations, and management reports. Pracco provides both as part of a single engagement.',
  },
];

const AccountingPage = () => (
  <>
    <Helmet>
      <title>Accounting & Bookkeeping Services India — Monthly, Affordable | Pracco</title>
      <meta name="description" content="Outsource your accounting & bookkeeping to Pracco. Monthly bookkeeping, financial statements, payroll, TDS compliance & MIS reports. Expert CAs. Affordable monthly plans. Free consultation." />
      <meta name="keywords" content="accounting services India, bookkeeping services India, outsource accounting India, monthly bookkeeping, small business accounting India, Tally accounting, payroll processing India, TDS filing, financial statements India, MIS reports" />
      <link rel="canonical" href="https://pracco.in/accounting-bookkeeping" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pracco.in/" },
          { "@type": "ListItem", "position": 2, "name": "Accounting & Bookkeeping", "item": "https://pracco.in/accounting-bookkeeping" },
        ],
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How Pracco's Accounting Service Works",
        "description": "Step-by-step process for outsourced accounting and bookkeeping with Pracco.",
        "step": STEPS.map((s) => ({
          "@type": "HowToStep",
          "position": parseInt(s.step),
          "name": s.title,
          "text": s.desc,
        })),
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      })}</script>
    </Helmet>

    {/* ── Mini Header ── */}
    <Box component="header" sx={{ px: { xs: 2, md: 6 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, background: C.white }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
        <Box component="img" src={logo} alt="Pracco" sx={{ height: 36 }} />
      </Link>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{ background: C.whatsapp, color: C.white, fontFamily: F.body, fontWeight: 600, fontSize: 13, px: 2.5, py: 1, borderRadius: 2, textTransform: 'none', '&:hover': { background: C.whatsappDk } }}
      >
        Free Consultation
      </Button>
    </Box>

    {/* ── Hero ── */}
    <Box component="section" sx={{ background: 'linear-gradient(160deg, #eff6ff 0%, #F8FAFC 55%, #e0f2fe 100%)', px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 9 }, textAlign: 'center' }}>
      <Typography component="h1" sx={{ fontFamily: F.heading, fontSize: { xs: 28, md: 44 }, fontWeight: 800, color: C.text, lineHeight: 1.15, mb: 2 }}>
        Accounting &amp; Bookkeeping Services in India
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: { xs: 15, md: 18 }, color: C.textMid, maxWidth: 680, mx: 'auto', lineHeight: 1.7, mb: 4 }}>
        Outsource your accounting to Pracco. Monthly bookkeeping, payroll, TDS compliance, and MIS reports —
        handled by expert CAs. Affordable plans for startups and SMEs. Free consultation.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          component="a"
          href="https://wa.me/9311817126"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<WhatsAppIcon />}
          sx={{ background: G.blue, color: C.white, fontFamily: F.body, fontWeight: 700, fontSize: 15, px: 4, py: 1.5, borderRadius: 2.5, textTransform: 'none', boxShadow: SH.btnBlue, '&:hover': { boxShadow: SH.btnBlueHover } }}
        >
          Get Free Consultation
        </Button>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          sx={{ fontFamily: F.body, fontWeight: 600, fontSize: 14, px: 3, py: 1.5, borderRadius: 2.5, textTransform: 'none', borderColor: C.blue, color: C.blue, '&:hover': { background: C.blueLight } }}
        >
          View All Services
        </Button>
      </Box>
    </Box>

    {/* ── Service Types ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        Our Accounting Services
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        Complete accounting and bookkeeping support — from daily entries to year-end financial statements.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1100, mx: 'auto' }}>
        {SERVICES.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.title}>
            <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 3, p: 3, height: '100%', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: SH.cardHover } }}>
              <CheckCircleOutlineIcon sx={{ color: C.blue, mb: 1 }} />
              <Typography sx={{ fontFamily: F.heading, fontSize: 16, fontWeight: 700, color: C.text, mb: 1 }}>{s.title}</Typography>
              <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── Process Steps ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.bg }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        How Our Accounting Service Works
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        Simple onboarding, dedicated accountant, monthly reports — zero hassle.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
        {STEPS.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.step}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Typography sx={{ fontFamily: F.heading, fontSize: 34, fontWeight: 800, color: C.border, lineHeight: 1, minWidth: 44 }}>{s.step}</Typography>
              <Box>
                <Typography sx={{ fontFamily: F.heading, fontSize: 15, fontWeight: 700, color: C.text, mb: 0.5 }}>{s.title}</Typography>
                <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── FAQ ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white, maxWidth: 860, mx: 'auto' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 4, textAlign: 'center' }}>
        Frequently Asked Questions — Accounting &amp; Bookkeeping
      </Typography>
      {FAQS.map((faq) => (
        <Accordion key={faq.q} disableGutters elevation={0} sx={{ border: `1px solid ${C.border}`, mb: 1.5, borderRadius: '10px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: C.blue }} />}>
            <Typography sx={{ fontFamily: F.body, fontWeight: 600, fontSize: 15, color: C.text }}>{faq.q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.8 }}>{faq.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>

    {/* ── CTA ── */}
    <Box component="section" sx={{ background: G.navyBlue, px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 7 }, textAlign: 'center' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.white, mb: 1.5 }}>
        Outsource Your Accounting Today
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 480, mx: 'auto' }}>
        Affordable monthly plans. Dedicated CA. No hidden charges. Start with a free consultation.
      </Typography>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{ background: C.whatsapp, color: C.white, fontFamily: F.body, fontWeight: 700, fontSize: 16, px: 5, py: 1.75, borderRadius: 2.5, textTransform: 'none', boxShadow: SH.btnGreen, '&:hover': { background: C.whatsappDk, boxShadow: SH.btnGreenHover } }}
      >
        WhatsApp Us Now
      </Button>
    </Box>

    {/* ── Mini Footer ── */}
    <Box component="footer" sx={{ px: { xs: 2.5, md: 8 }, py: 3, background: C.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      <Typography sx={{ fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
        © {new Date().getFullYear()} Pracco Corporate Services Pvt. Ltd. — info@pracco.in · +91-9311817126
      </Typography>
      <Link to="/" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
        ← Back to Home
      </Link>
    </Box>
  </>
);

export default AccountingPage;
